// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateShopByLookProduct from "@bg/App/Handlers/FindProductByErp/UpdateShopByLookProduct";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { ProductPriceStyles, productPriceStyles } from "@bg/Overrides/Components/ProductPrice";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { AddToCartButtonStyles, productAddToCartButtonStyles } from "@bgs/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, {
    ProductQtyOrderedStyles,
    productQuantityOrderedStyles,
} from "@bgs/Overrides/Components/ProductQuantityOrdered";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import LazyImage from "@insite/mobius/LazyImage";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productErp: string;
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        product: state.shopByLook.byErp[props.productErp]?.product,
        productInfo: state.shopByLook.byErp[props.productErp]?.productInfo,
        isLoading: state.shopByLook.isLoading[props.productErp],
    };
};

const mapDispatchToProps = {
    updateShopByLookProduct,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ShopByLookProductStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    descriptionWrapper?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    productPrice?: ProductPriceStyles;
    productQty?: ProductQtyOrderedStyles;
    addToCart?: AddToCartButtonStyles;
}

export const styles: ShopByLookProductStyles = {
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    productImage: {
        wrapper: {
            css: css`
                margin-right: 10px;
                max-width: 118px;
            `,
        },
        linkWrappingImage: {
            css: css`
                height: 118px;
                width: 118px;
                && img {
                    object-fit: contain;
                }
            `,
        },
        image: {
            cloudinaryWidth: "118",
        } as any,
    } as any,
    descriptionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                weight: fontConfig.weight.semiBold,
                size: fontConfig.fontSize.small,
                lineHeight: "17px",
                css: css`
                    letter-spacing: 1px;
                    height: 34px;
                `,
            },
            css: css`
                margin-bottom: 24px;
            `,
        },
        productDetailDescription: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.semiBold,
            lineHeight: "17px",
            css: css`
                letter-spacing: 1px;
                height: 34px;
                margin-bottom: 24px;
            `,
        },
    },
    productPrice: {
        ...productPriceStyles,
        wrapper: {
            ...productPriceStyles.wrapper,
            css: css`
                margin-bottom: 10px;
            `,
        },
        price: {
            ...productPriceStyles.price,
            priceText: {
                size: fontConfig.fontSize.small,
                weight: fontConfig.weight.semiBold,
                lineHeight: "17px",
                css: css`
                    letter-spacing: 1px;
                `,
            },
            nonStockText: {
                css: css`
                    ${productPriceStyles.price?.nonStockText?.css}
                    max-width: 270px;
                `,
            },
        },
    },
    productQty: {
        ...productQuantityOrderedStyles,
        wrapper: {
            css: css`
                ${productQuantityOrderedStyles.wrapper?.css}
                height: 36px;
                &&&&& input {
                    height: 32px;
                }
            `,
        },
    },
    addToCart: {
        asButton: {
            css: css`
                ${productAddToCartButtonStyles.asButton?.css}
                &&&& {
                    min-height: 36px;
                    height: 36px;
                }
            `,
        },
    },
};

const ShopByLookProduct: React.FC<Props> = ({
    product,
    productInfo,
    isLoading,
    productErp,
    updateShopByLookProduct,
}) => {
    if (isLoading) {
        return (
            <StyledWrapper
                css={css`
                    height: 50px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner size={22} />
            </StyledWrapper>
        );
    }

    if (!isLoading && (!product || !productInfo)) {
        return <Typography>{translate("Product Not Found")}</Typography>;
    }

    const updateQuantity = (qty: number) => {
        updateShopByLookProduct({ isQty: true, qty, productErp });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: () => {},
        onQtyOrderedChanged: updateQuantity,
    };

    const modifiedProduct = {
        ...product,
        productUri: product.productDetailUrl,
        shortDescription: product.shortDescription,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <ProductImage product={modifiedProduct} extendedStyles={styles.productImage} />
                <StyledWrapper {...styles.descriptionWrapper}>
                    <ProductDescription
                        product={modifiedProduct}
                        {...({ lines: 2, lineHeight: 17 } as any)}
                        extendedStyles={styles.productDescription}
                    />
                    <ProductPrice
                        showLabel={false}
                        product={productContext}
                        extendedStyles={styles.productPrice as any}
                    />
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        <ProductQuantityOrdered extendedStyles={styles.productQty} />
                        <ProductAddToCartButton
                            {...({ iconOnly: false } as any)}
                            sizeVariant="small"
                            extendedStyles={styles.addToCart}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ShopByLookProduct);
